import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
    BUSINESS_TYPES,
    INTAKE_STEPS,
    VISITOR_ACTIONS,
    classifyOpportunity,
    clearIntakeDraft,
    getEmptyIntakeForm,
    loadIntakeDraft,
    saveIntakeDraft,
    submitIntakeBrief,
    validateStep,
} from '../helpers/clientIntakeHelper';
import loggerHelper from '../helpers/loggerHelper';
import { applyRouteSeo } from '../helpers/seoHelper';

const CONTENT_ITEMS = [
    ['hasBusinessPhone', 'Business phone'],
    ['hasAddressOrMaps', 'Address or map link'],
    ['hasServicesList', 'Services or menu'],
    ['hasPhotos', 'Business photos'],
    ['hasLogo', 'Logo'],
    ['hasTimings', 'Opening hours'],
];

const EXTRA_FEATURES = [
    ['needsOnlinePayments', 'Online payments'],
    ['needsBookingSystem', 'Online booking'],
    ['needsMultiLocation', 'Multiple locations'],
    ['needsMultiLanguage', 'Multiple languages'],
    ['needsCrmOrPortal', 'Customer login or portal'],
];

const ACTION_SUGGESTIONS = {
    'Shop / Retail': ['call', 'whatsapp', 'visit'],
    'Clinic / Healthcare': ['call', 'whatsapp', 'book'],
    'Restaurant / Cafe': ['menu', 'call', 'directions'],
    'Consultant / Professional': ['form', 'call', 'whatsapp'],
    'Real Estate': ['form', 'call', 'portfolio'],
    'Salon / Spa': ['book', 'whatsapp', 'menu'],
    'Gym / Fitness': ['form', 'call', 'visit'],
    Photographer: ['portfolio', 'form', 'whatsapp'],
    'Education / Tutor': ['form', 'call', 'whatsapp'],
};

const Choice = ({ selected, onClick, children, compact = false }) => (
    <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        sx={{
            appearance: 'none',
            width: '100%',
            minHeight: compact ? 48 : 58,
            px: compact ? 1.7 : 2,
            py: compact ? 1.1 : 1.4,
            borderRadius: 2.2,
            border: selected ? '1.5px solid #20B8B5' : '1px solid rgba(16,42,58,0.12)',
            bgcolor: selected ? 'secondary.light' : '#FBFDFD',
            color: selected ? 'secondary.dark' : 'text.primary',
            font: 'inherit',
            fontWeight: selected ? 750 : 620,
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            transition: 'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
            '&:hover': { borderColor: 'secondary.main', transform: 'translateY(-1px)' },
            '&:focus-visible': { outline: '3px solid rgba(32,184,181,0.24)', outlineOffset: 2 },
        }}
    >
        <Typography component="span" variant={compact ? 'body2' : 'body1'} fontWeight="inherit">{children}</Typography>
        <Box sx={{ width: 22, height: 22, borderRadius: '50%', border: selected ? 0 : '1px solid rgba(16,42,58,0.22)', bgcolor: selected ? 'secondary.main' : 'transparent', color: 'white', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            {selected && <CheckRoundedIcon sx={{ fontSize: 16 }} />}
        </Box>
    </Box>
);

const ClientIntake = () => {
    const { pathname } = useLocation();
    const [activeStep, setActiveStep] = useState(0);
    const [form, setForm] = useState(getEmptyIntakeForm);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => applyRouteSeo(pathname), [pathname]);

    useEffect(() => {
        const draft = loadIntakeDraft();
        if (draft) setForm(draft);
    }, []);

    useEffect(() => {
        saveIntakeDraft(form);
    }, [form]);

    const stepId = INTAKE_STEPS[activeStep].id;
    const progress = ((activeStep + 1) / INTAKE_STEPS.length) * 100;

    const updateField = (name, value) => {
        setForm((previous) => ({ ...previous, [name]: value }));
        if (errors[name]) {
            setErrors((previous) => {
                const next = { ...previous };
                delete next[name];
                return next;
            });
        }
    };

    const toggleArrayValue = (name, value) => {
        setForm((previous) => {
            const current = previous[name] || [];
            return { ...previous, [name]: current.includes(value) ? current.filter((item) => item !== value) : [...current, value] };
        });
        if (errors[name]) setErrors((previous) => ({ ...previous, [name]: undefined }));
    };

    const chooseBusinessType = (businessType) => {
        setForm((previous) => ({
            ...previous,
            businessType,
            visitorActions: previous.visitorActions.length ? previous.visitorActions : (ACTION_SUGGESTIONS[businessType] || ['call', 'whatsapp', 'form']),
        }));
        setErrors((previous) => ({ ...previous, businessType: undefined }));
    };

    const handleNext = () => {
        const nextErrors = validateStep(stepId, form);
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            return;
        }
        setErrors({});
        setActiveStep((previous) => Math.min(previous + 1, INTAKE_STEPS.length - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBack = () => {
        setErrors({});
        setActiveStep((previous) => Math.max(previous - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async () => {
        for (const requiredStep of ['contact', 'goals']) {
            const nextErrors = validateStep(requiredStep, form);
            if (Object.keys(nextErrors).length) {
                setErrors(nextErrors);
                setActiveStep(INTAKE_STEPS.findIndex((step) => step.id === requiredStep));
                return;
            }
        }

        if (form.preferredContact === 'Email' && !form.email.trim()) {
            setErrors({ email: 'Add an email address or choose WhatsApp / phone' });
            return;
        }

        setSubmitting(true);
        setSubmitError('');
        try {
            await submitIntakeBrief(form);
            clearIntakeDraft();
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            setSubmitError(error.message || 'Could not send your details. Please try again.');
            loggerHelper.error('Client intake submit failed', { message: error.message });
        } finally {
            setSubmitting(false);
        }
    };

    const renderBusinessStep = () => (
        <Stack spacing={4}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Your name" autoComplete="name" value={form.customerName} onChange={(event) => updateField('customerName', event.target.value)} error={Boolean(errors.customerName)} helperText={errors.customerName} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Business name" autoComplete="organization" value={form.businessName} onChange={(event) => updateField('businessName', event.target.value)} error={Boolean(errors.businessName)} helperText={errors.businessName} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="WhatsApp / phone" type="tel" autoComplete="tel" value={form.phone} onChange={(event) => updateField('phone', event.target.value)} error={Boolean(errors.phone)} helperText={errors.phone || 'We only use this to discuss your website'} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="City or locality" autoComplete="address-level2" value={form.locality} onChange={(event) => updateField('locality', event.target.value)} error={Boolean(errors.locality)} helperText={errors.locality} /></Grid>
            </Grid>
            <Box>
                <Typography fontWeight={760} sx={{ mb: 0.6 }}>What kind of business is it?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>One tap is enough. This also suggests the most useful website actions.</Typography>
                <Grid container spacing={1.2}>
                    {BUSINESS_TYPES.map((type) => <Grid item xs={12} sm={6} key={type}><Choice compact selected={form.businessType === type} onClick={() => chooseBusinessType(type)}>{type}</Choice></Grid>)}
                </Grid>
                {errors.businessType && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{errors.businessType}</Typography>}
            </Box>
        </Stack>
    );

    const renderGoalsStep = () => (
        <Stack spacing={4}>
            <TextField
                fullWidth
                label="Main services or products"
                placeholder="For example: haircuts, facials, bridal makeup"
                value={form.mainServices}
                onChange={(event) => updateField('mainServices', event.target.value)}
                error={Boolean(errors.mainServices)}
                helperText={errors.mainServices || 'A short comma-separated list is perfect'}
            />
            <Box>
                <Typography fontWeight={760} sx={{ mb: 0.6 }}>What should visitors do?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>We selected common choices for your business. Change them with a tap.</Typography>
                <Grid container spacing={1.2}>
                    {VISITOR_ACTIONS.map((action) => <Grid item xs={12} sm={6} key={action.id}><Choice compact selected={form.visitorActions.includes(action.id)} onClick={() => toggleArrayValue('visitorActions', action.id)}>{action.label}</Choice></Grid>)}
                </Grid>
                {errors.visitorActions && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{errors.visitorActions}</Typography>}
            </Box>
            <Box>
                <Typography fontWeight={760} sx={{ mb: 1.5 }}>Do you already have a website?</Typography>
                <Stack direction="row" spacing={1.2}>
                    {['No', 'Yes', 'Under construction'].map((item) => <Choice key={item} compact selected={form.hasWebsite === item} onClick={() => updateField('hasWebsite', item)}>{item}</Choice>)}
                </Stack>
            </Box>
        </Stack>
    );

    const renderContentStep = () => (
        <Stack spacing={4}>
            <Box>
                <Typography fontWeight={760} sx={{ mb: 0.6 }}>What do you already have?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select everything ready now. You can send the files later on WhatsApp.</Typography>
                <Grid container spacing={1.2}>
                    {CONTENT_ITEMS.map(([key, label]) => <Grid item xs={12} sm={6} key={key}><Choice compact selected={Boolean(form[key])} onClick={() => updateField(key, !form[key])}>{label}</Choice></Grid>)}
                </Grid>
            </Box>
            <Box>
                <Typography fontWeight={760} sx={{ mb: 1.5 }}>When can you share the content?</Typography>
                <Grid container spacing={1.2}>
                    {['Same day', '1-2 days', 'This week', 'Need help collecting'].map((item) => <Grid item xs={6} key={item}><Choice compact selected={form.contentReadyIn === item} onClick={() => updateField('contentReadyIn', item)}>{item}</Choice></Grid>)}
                </Grid>
            </Box>
            <Box>
                <Typography fontWeight={760} sx={{ mb: 0.6 }}>Need anything beyond a simple website?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Leave these unselected if you only need the four-page starter site.</Typography>
                <Grid container spacing={1.2}>
                    {EXTRA_FEATURES.map(([key, label]) => <Grid item xs={12} sm={6} key={key}><Choice compact selected={Boolean(form[key])} onClick={() => updateField(key, !form[key])}>{label}</Choice></Grid>)}
                </Grid>
            </Box>
            <Box>
                <Typography fontWeight={760} sx={{ mb: 1.5 }}>Preferred launch timing</Typography>
                <Grid container spacing={1.2}>
                    {['As soon as possible', 'Within 2 weeks', 'Within a month', 'Just exploring'].map((item) => <Grid item xs={12} sm={6} key={item}><Choice compact selected={form.goLiveWhen === item} onClick={() => updateField('goLiveWhen', item)}>{item}</Choice></Grid>)}
                </Grid>
            </Box>
        </Stack>
    );

    const renderReviewStep = () => {
        const classification = classifyOpportunity(form);
        const selectedActions = form.visitorActions.map((id) => VISITOR_ACTIONS.find((item) => item.id === id)?.label).filter(Boolean);
        const readyCount = CONTENT_ITEMS.filter(([key]) => form[key]).length;

        return (
            <Stack spacing={3}>
                <Alert severity={classification.expressFit ? 'success' : 'info'} sx={{ borderRadius: 2 }}>
                    <Typography fontWeight={760}>Recommended: {classification.type}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.4 }}>{classification.reason}</Typography>
                </Alert>
                <Grid container spacing={1.5}>
                    {[
                        ['Business', form.businessName],
                        ['Type', form.businessType],
                        ['Location', form.locality],
                        ['Services', form.mainServices],
                        ['Visitor actions', selectedActions.join(', ')],
                        ['Content ready', `${readyCount} of ${CONTENT_ITEMS.length} items`],
                    ].map(([label, value]) => (
                        <Grid item xs={12} sm={6} key={label}>
                            <Box sx={{ height: '100%', p: 2, borderRadius: 2.2, bgcolor: '#F7FBFC', border: '1px solid rgba(16,42,58,0.09)' }}>
                                <Typography variant="caption" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase' }}>{label}</Typography>
                                <Typography sx={{ mt: 0.5, fontWeight: 660, lineHeight: 1.5 }}>{value || 'Not provided'}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                <Box>
                    <Typography fontWeight={760} sx={{ mb: 1.5 }}>How should we contact you?</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                        {['WhatsApp', 'Phone call', 'Email'].map((item) => <Choice compact key={item} selected={form.preferredContact === item} onClick={() => updateField('preferredContact', item)}>{item}</Choice>)}
                    </Stack>
                </Box>
                {form.preferredContact === 'Email' && <TextField fullWidth label="Email address" type="email" autoComplete="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} error={Boolean(errors.email)} helperText={errors.email} />}
                <TextField fullWidth label="Anything else? (optional)" placeholder="Only add a note if there is something important we should know" multiline minRows={2} value={form.extraNotes} onChange={(event) => updateField('extraNotes', event.target.value)} />
                <Typography variant="body2" color="text.secondary">By sending this request, your details are emailed to DevCraft. Domain and hosting are purchased separately in your name.</Typography>
                {submitError && <Alert severity="error">{submitError}</Alert>}
            </Stack>
        );
    };

    const renderStep = () => {
        if (stepId === 'contact') return renderBusinessStep();
        if (stepId === 'goals') return renderGoalsStep();
        if (stepId === 'content') return renderContentStep();
        return renderReviewStep();
    };

    if (submitted) {
        return (
            <Box sx={{ minHeight: '72vh', bgcolor: '#F7FBFC', py: { xs: 8, md: 12 } }}>
                <Container maxWidth="sm">
                    <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: '1px solid rgba(16,42,58,0.1)', textAlign: 'center' }}>
                        <Box sx={{ width: 58, height: 58, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'secondary.light', color: 'secondary.dark', mx: 'auto', mb: 2.5 }}><CheckRoundedIcon sx={{ fontSize: 30 }} /></Box>
                        <Typography component="h1" sx={{ fontSize: { xs: '1.9rem', md: '2.35rem' }, fontWeight: 820, letterSpacing: '-0.04em' }}>Your website request is in.</Typography>
                        <Typography sx={{ mt: 1.5, color: 'text.secondary', lineHeight: 1.7 }}>Thanks {form.customerName || 'there'}. We’ll review the details and contact you through {form.preferredContact.toLowerCase()}.</Typography>
                        <Button variant="outlined" onClick={() => { setSubmitted(false); setForm(getEmptyIntakeForm()); setActiveStep(0); clearIntakeDraft(); }} sx={{ mt: 3 }}>Start another request</Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F7FBFC', py: { xs: 5, md: 8 } }}>
            <Container maxWidth="md">
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={2} sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="overline" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.4 }}>DevCraft Express</Typography>
                        <Typography component="h1" sx={{ mt: 0.5, fontSize: { xs: '2.25rem', md: '3.25rem' }, lineHeight: 1.08, fontWeight: 840, letterSpacing: '-0.05em' }}>Start your website.</Typography>
                        <Typography sx={{ mt: 1, color: 'text.secondary' }}>Four short steps. Most answers take one tap.</Typography>
                    </Box>
                    <Chip label="About 2 minutes" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, bgcolor: 'secondary.light', color: 'secondary.dark', fontWeight: 750 }} />
                </Stack>

                <Box sx={{ mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={750}>{INTAKE_STEPS[activeStep].title}</Typography>
                        <Typography variant="body2" color="text.secondary">{activeStep + 1} of {INTAKE_STEPS.length}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 7, borderRadius: 5, bgcolor: 'rgba(16,42,58,0.08)', '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: 'secondary.main' } }} />
                </Box>

                <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5, md: 4.5 }, borderRadius: 3.5, border: '1px solid rgba(16,42,58,0.1)', bgcolor: 'white' }}>
                    <Typography component="h2" sx={{ fontSize: { xs: '1.45rem', md: '1.8rem' }, fontWeight: 800, letterSpacing: '-0.025em' }}>{INTAKE_STEPS[activeStep].title}</Typography>
                    <Typography sx={{ mt: 0.7, mb: 3.5, color: 'text.secondary' }}>{INTAKE_STEPS[activeStep].subtitle}</Typography>
                    {renderStep()}
                </Paper>

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5 }}>
                    <Button startIcon={<ArrowBackRoundedIcon />} onClick={handleBack} disabled={activeStep === 0} sx={{ px: 0 }}>Back</Button>
                    {activeStep < INTAKE_STEPS.length - 1 ? (
                        <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} onClick={handleNext} sx={{ px: 3, py: 1.25 }}>Continue</Button>
                    ) : (
                        <Button variant="contained" endIcon={<SendRoundedIcon />} onClick={handleSubmit} disabled={submitting} sx={{ px: 3, py: 1.25 }}>{submitting ? 'Sending…' : 'Send request'}</Button>
                    )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>Your progress is saved automatically on this device.</Typography>
            </Container>
        </Box>
    );
};

export default ClientIntake;
